import type { StylePreset } from './stylePresets';
import type { ShareVisibility } from '@utils/shareVisibility';
import { normalizePricingBreakdown } from '@utils/pricingBreakdownUtils';

export type PricingBreakdown = {
	min: number;
	mid: number;
	max: number;
	materials: Array<{ name: string; quantity: number; unit: string; unitPrice: number; total: number }>;
	labor: Array<{ name: string; hours: number; ratePerHour: number; total: number }>;
};

export type Tool = {
	name: string;
	description: string;
};

export type AIGeneratedContent = {
	photoAfterUrl: string; // Transformed image
	contentLanguage?: 'en' | 'de' | 'hr';
	planDescription: string;
	keyFeatures: string[];
	instructions: string[];
	timeline_tasks?: Array<{
		title: string;
		description: string;
		dayStart: number;
		dayEnd: number;
		dependsOnSteps?: number[];
		seasonal_ban_candidate?: boolean;
		seasonal_ban_reason?: string;
		required_skills_for_step?: string[];
		required_gardeners_for_step?: number;
	}>;
	tools?: Tool[] | string[]; // Optional for backward compatibility with older plans (string[]) and new format (Tool[])
	pricingBreakdown: PricingBreakdown;
	/** AI-derived skills needed to execute this plan (used in offer wizard). Omitted for older plans. */
	required_skills?: string[];
	/** AI-recommended number of workers/gardeners to execute the plan. Omitted for older plans. */
	recommended_team_size?: number;
	/** Optional per-instruction metadata for skills and team size, aligned by index with instructions[]. */
	instruction_metadata?: Array<{
		required_skills_for_step: string[];
		required_gardeners_for_step: number;
	}>;
	selected_component_coverage?: {
		selected: string[];
		included: string[];
		missing: string[];
		uncertain?: string[];
		source?: 'ai_image_review' | 'plan_text';
	};
	generation_warnings?: string[];
	generatedAt: string;
};

/** One revision in plan history (full snapshot of a generated design) */
export type PlanRevision = AIGeneratedContent & {
	editInstructions?: string;
};

// AI Analysis types
export type DetectedSize = {
	value: number;
	confidence: number;
	reasoning: string;
};

export type DetectedFeature = {
	name: string;
	confidence: number;
	position?: string;
	condition?: 'good' | 'fair' | 'poor' | 'needs_replacement';
};

/** When set, this question is only shown when the group's gate question (first yes_no) has this answer. */
export type ShowWhenGate = { gateAnswer: 'yes' };

export type AIGeneratedQuestion = {
	question: string;
	type: 'multiple_choice' | 'yes_no' | 'text' | 'number';
	options?: string[];
	/** If true, user can select multiple options (stored as string[]). Only for multiple_choice. */
	allowMultiple?: boolean;
	/** AI-recommended answer(s) based on image/context. Single value or array for multi-select. */
	recommendedAnswers?: string[];
	priority: 'high' | 'medium' | 'low';
	reasoning: string;
	/** Optional group id for visual grouping and conditional logic (e.g. 'fence', 'pool', 'ai'). */
	group?: string;
	/** When set, show this question only when the gate question in the same group has this answer. */
	showWhen?: ShowWhenGate;
};

export type GardenConstraints = {
	slope?: 'none' | 'slight' | 'moderate' | 'steep';
	lighting?: 'full_sun' | 'partial_sun' | 'shade';
	soil?: string;
	drainage?: 'good' | 'moderate' | 'poor';
};

export type StyleHint = {
	style: StylePreset['key'];
	confidence: number;
	reasoning: string;
};

export type RecommendedComponent = {
	componentId: string;
	reasoning: string;
	priority: 'high' | 'medium' | 'low';
};

export type ComponentFitLevel = 'good' | 'tight' | 'not_recommended';

export type SpaceType = 'garden' | 'terrace' | 'balcony' | 'unknown';

export type ComponentFeasibility = {
	componentId: string;
	fit: ComponentFitLevel;
	reasoning: string;
	suggestedAlternative?: string;
};

export type LayoutCapacity = {
	maxMajorComponents?: number;
	layoutComplexityWarning?: string;
};

export type AIAnalysis = {
	/** If false, the image was not recognized as a garden; show a friendly FYI but allow continuing. */
	isGarden?: boolean;
	/** Short message when isGarden is false (e.g. "No garden recognized. Try a different angle or a clearer picture."). */
	gardenRecognitionNote?: string;
	detectedSize?: DetectedSize;
	detectedFeatures?: DetectedFeature[];
	suggestedQuestions?: AIGeneratedQuestion[];
	constraints?: GardenConstraints;
	styleHints?: StyleHint[];
	recommendedComponents?: RecommendedComponent[];
	/** Broad space type inferred from image (helps feasibility guidance). */
	spaceType?: SpaceType;
	/** Feasibility assessment for components in this specific space. */
	componentFeasibility?: ComponentFeasibility[];
	/** Capacity guidance for large/complex combinations in current space. */
	layoutCapacity?: LayoutCapacity;
};

/** Keys for "How you'll use the garden" multi-select (stored in DB as garden_uses). */
export type GardenUseKey = 'relaxing' | 'growing_food' | 'kids_pets' | 'hosting' | 'dining_outdoors';
export type ProjectFocusKey = 'planting_only' | 'construction_hardscape_only' | 'mixed_planting_construction';

export type QuestionnaireData = {
	gardenSize: number; // m²
	gardenShape?: 'rectangular' | 'l-shaped' | 'irregular' | 'square' | 'circular';
	selectedComponents?: string[]; // Array of selected garden components (e.g., ['pool', 'fence', 'bbq'])
	photoBeforeUrl?: string;
	styleKey: StylePreset['key'];
	needsWants: string;
	maintenanceLevel?: 'low' | 'medium' | 'high';
	childrenPets?: 'none' | 'children' | 'pets' | 'both';
	budgetRange?: { min: number; max: number };
	/** Slider 0–100: 0 = value-focused / budget-friendly, 100 = premium. Used when no budget range is set. */
	budgetPreference?: number;
	timeline: string;
	projectStartDate?: string; // ISO date string - when seeker is ready to start
	location: string;
	// New fields for scaling components
	householdSize?: number; // Number of people currently in the household
	gardenUsers?: number; // Number of people who will use the garden (can be different from household size if planning for future)
	plantDensityPreference?: 'minimal' | 'moderate' | 'dense'; // Preference for amount of plants/flowers vs open space
	/** High-level scope preference for contractors (planting vs construction/hardscape). */
	projectFocus?: ProjectFocusKey;
	/** How the garden will be used (multi-select). Used for design focus and to show hosting question. */
	gardenUses?: GardenUseKey[];
	/** When hosting is a use: max number of people to accommodate. Design uses max(gardenUsers, hostingMaxPeople). */
	hostingMaxPeople?: number;
	/** When kids_pets is a use: optional details for design (play space, dog house, safe plants). */
	kidsCount?: number;
	kidsAges?: string;
	petsDescription?: string;
	// AI analysis fields
	aiAnalysis?: AIAnalysis;
	/** User corrections/comments on the AI analysis (e.g. "No fence", "There is slope in the back", "Size is about 80m²") */
	aiAnalysisComments?: string;
	aiAnswers?: Record<string, any>; // Answers to AI-generated questions
	/** Per-question comments/clarifications from the seeker (e.g. if a question was unclear or not specific enough) */
	aiQuestionComments?: Record<string, string>; // key = question index as string, value = comment text
	userCorrections?: Array<{ field: string; aiValue: any; userValue: any }>; // Track what user corrected
};

export type PlanModerationStatus = 'visible' | 'hidden' | 'deleted';

export type PublicPlan = {
	id: string; // Frontend string ID (e.g., "plscwacq")
	databaseId?: number; // Database numeric ID (for API calls)
	seekerUserId: string;
	/** Whether current viewer can edit this plan (used for shared direct plans). Defaults to true. */
	canEdit?: boolean;
	/** Direct-plan metadata (for company views) */
	hasClientInvite?: boolean;
	hasLinkedSeeker?: boolean;
	/** Seeker-side invite metadata for shared direct plans */
	inviteId?: number;
	inviteCode?: string;
	inviteStatus?: 'pending' | 'used' | 'expired' | 'cancelled';
	sharedByCompanyName?: string;
	share_visibility?: ShareVisibility;
	title: string;
	city: string;
	contactEmail: string;
	styleKey: StylePreset['key'];
	notes?: string;
	photoBeforeUrl?: string;
	status: 'draft' | 'active' | 'closed';
	createdAt: string;
	// New fields for questionnaire and AI-generated content
	questionnaire?: QuestionnaireData;
	aiGenerated?: AIGeneratedContent;
	/** History of AI-generated designs (each "Regenerate with changes" adds one) */
	revisions?: PlanRevision[];
	moderationStatus?: PlanModerationStatus;
};
  

function normalizePlan(plan: PublicPlan): PublicPlan {
	return {
		...plan,
		moderationStatus: plan.moderationStatus ?? 'visible'
	};
}

// Load initial plans from localStorage or use defaults
function loadPublicPlans(): PublicPlan[] {
	const stored = localStorage.getItem('publicPlans');
	if (stored) {
		try {
			const parsed = JSON.parse(stored) as PublicPlan[];
			return parsed.map(normalizePlan);
		} catch {
			// If parsing fails, use defaults
		}
	}
	// Default seed data
	const defaults: PublicPlan[] = [
		{
			id: 'pl1',
			seekerUserId: 'u-seeker-1',
			title: 'Small modern backyard',
			city: 'Berlin',
			contactEmail: 'masked@example.com',
			styleKey: 'modern',
			notes: 'Simple seating, low maintenance.',
			photoBeforeUrl: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&h=400&fit=crop',
			status: 'active',
			createdAt: new Date(Date.now() - 86400000 * 2).toISOString(),
			moderationStatus: 'visible'
		},
		{
			id: 'pl2',
			seekerUserId: 'u-seeker-2',
			title: 'Cozy cottage front',
			city: 'Munich',
			contactEmail: 'masked@example.com',
			styleKey: 'cottage',
			photoBeforeUrl: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800&h=400&fit=crop',
			status: 'active',
			createdAt: new Date(Date.now() - 86400000 * 1).toISOString(),
			moderationStatus: 'visible',
			aiGenerated: {
				photoAfterUrl: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800&h=400&fit=crop',
				planDescription: 'A romantic cottage-style planting plan with layered perennials and warm lighting.',
				keyFeatures: ['Stone pathway with herb borders', 'Climbing roses on trellis', 'Cozy seating nook'],
				instructions: [
					'Install trellis and prep soil along facade.',
					'Plant layered perennial mix with seasonal color.',
					'Add solar lighting and seating to highlight focal points.'
				],
				tools: [
					{ name: 'Measuring tape', description: 'For creating natural, curving pathways and informal garden layouts' },
					{ name: 'Garden fork', description: 'For soil preparation, essential for the rich, fertile soil required by cottage garden plants' },
					{ name: 'Pruning shears', description: 'For planting preparation and ongoing maintenance of the lush, informal planting' }
				],
				pricingBreakdown: {
					min: 1800,
					mid: 2400,
					max: 3200,
					materials: [
						{ name: 'Perennials & shrubs', quantity: 25, unit: 'pcs', unitPrice: 35, total: 875 },
						{ name: 'Lighting kit', quantity: 1, unit: 'set', unitPrice: 250, total: 250 }
					],
					labor: [
						{ name: 'Installation crew', hours: 28, ratePerHour: 45, total: 1260 }
					]
				},
				generatedAt: new Date(Date.now() - 86400000 * 1).toISOString()
			}
		}
	];
	return defaults.map(normalizePlan);
}

export let publicPlans: PublicPlan[] = loadPublicPlans();

export let publicPlansById: Record<string, PublicPlan> =
	Object.fromEntries(publicPlans.map(p => [p.id, p])) as Record<string, PublicPlan>;

// Save to localStorage (non-critical - don't throw on quota errors)
function savePublicPlans() {
	try {
		localStorage.setItem('publicPlans', JSON.stringify(publicPlans));
	} catch (err: any) {
		// If quota exceeded, try to prune old plans
		if (err?.name === 'QuotaExceededError') {
			console.warn('localStorage quota exceeded, attempting to prune old plans...');
			try {
				// Keep only the 20 most recent plans
				const sorted = [...publicPlans].sort((a, b) => 
					new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
				);
				const toKeep = sorted.slice(0, 20);
				publicPlans = toKeep;
				publicPlansById = Object.fromEntries(toKeep.map(p => [p.id, p])) as Record<string, PublicPlan>;
				localStorage.setItem('publicPlans', JSON.stringify(publicPlans));
			} catch (pruneErr) {
				console.error('Failed to save public plans to localStorage (quota exceeded, pruning failed):', pruneErr);
				// Don't throw - just log the error and continue without localStorage
			}
		} else {
			console.error('Failed to save public plans to localStorage', err);
			// Don't throw for other errors either - localStorage is not critical
		}
	}
}

export function addPublicPlan(plan: PublicPlan) {
	const normalized = normalizePlan(plan);
	publicPlans.push(normalized);
	publicPlansById[plan.id] = normalized;
	try {
		savePublicPlans();
		// Dispatch custom event to notify components of changes
		window.dispatchEvent(new CustomEvent('publicPlansUpdated'));
	} catch (err) {
		// Roll back on failure so in-memory state stays in sync
		publicPlans = publicPlans.filter(p => p.id !== plan.id);
		delete publicPlansById[plan.id];
		throw err;
	}
}

export function getPublicPlans(): PublicPlan[] {
	// Reload from localStorage to get latest
	publicPlans = loadPublicPlans();
	publicPlansById = Object.fromEntries(publicPlans.map(p => [p.id, p])) as Record<string, PublicPlan>;
	return publicPlans;
}

export function pruneDemoPlans(countToKeep = 3) {
	const plans = getPublicPlans();
	const demoPlans = plans
		.filter(p => p.seekerUserId === 'u-seeker-demo')
		.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

	if (demoPlans.length <= countToKeep) {
		return;
	}

	const keep = new Set(demoPlans.slice(0, countToKeep).map(p => p.id));
	publicPlans = plans.filter(p => p.seekerUserId !== 'u-seeker-demo' || keep.has(p.id));
	publicPlansById = Object.fromEntries(publicPlans.map(p => [p.id, p])) as Record<string, PublicPlan>;
	savePublicPlans();
	window.dispatchEvent(new CustomEvent('publicPlansUpdated'));
}

export function updatePublicPlan(planId: string, updates: Partial<PublicPlan>) {
	const index = publicPlans.findIndex(p => p.id === planId);
	if (index !== -1) {
		const updated = normalizePlan({ ...publicPlans[index], ...updates });
		publicPlans[index] = updated;
		publicPlansById[planId] = updated;
		savePublicPlans();
		window.dispatchEvent(new CustomEvent('publicPlansUpdated'));
	}
}

export async function setPublicPlanModerationStatus(planId: string, moderationStatus: PlanModerationStatus) {
	const plan = publicPlans.find(p => p.id === planId);
	
	// If plan has a database ID, update via API
	if (plan?.databaseId) {
		try {
			const { updatePlanModerationStatus } = await import('@services/plans');
			await updatePlanModerationStatus(plan.databaseId, moderationStatus);
		} catch (err) {
			console.error('Failed to update plan moderation status via API:', err);
			// Continue with localStorage update as fallback
		}
	}
	
	updatePublicPlan(planId, { moderationStatus });
}

// Track deleted plan IDs (including database IDs) to prevent them from reappearing
function getDeletedPlanIds(): Set<string> {
	const stored = localStorage.getItem('deletedPlanIds');
	if (stored) {
		try {
			const parsed = JSON.parse(stored) as string[];
			return new Set(parsed);
		} catch {
			return new Set();
		}
	}
	return new Set();
}

function saveDeletedPlanIds(ids: Set<string>) {
	try {
		localStorage.setItem('deletedPlanIds', JSON.stringify(Array.from(ids)));
	} catch (err) {
		console.error('Failed to save deleted plan IDs to localStorage', err);
	}
}

function addDeletedPlanId(planId: string, databaseId?: number) {
	const deletedIds = getDeletedPlanIds();
	deletedIds.add(planId);
	if (databaseId) {
		deletedIds.add(String(databaseId));
		deletedIds.add(`db-${databaseId}`);
	}
	saveDeletedPlanIds(deletedIds);
}

export function isPlanDeleted(planId: string, databaseId?: number): boolean {
	const deletedIds = getDeletedPlanIds();
	return deletedIds.has(planId) || 
		   (databaseId !== undefined && deletedIds.has(String(databaseId))) ||
		   (databaseId !== undefined && deletedIds.has(`db-${databaseId}`));
}

export async function deletePublicPlan(planId: string) {
	const plan = publicPlans.find(p => p.id === planId);
	const index = publicPlans.findIndex(p => p.id === planId);
	if (index !== -1) {
		// If plan has a database ID, delete via API
		if (plan?.databaseId) {
			try {
				const { deletePlan } = await import('@services/plans');
				await deletePlan(plan.databaseId);
			} catch (err) {
				console.error('Failed to delete plan via API:', err);
				// Continue with localStorage deletion as fallback
			}
		}
		
		// Track the deleted plan ID (including database ID if it exists)
		addDeletedPlanId(planId, plan?.databaseId);
		publicPlans = publicPlans.filter(p => p.id !== planId);
		delete publicPlansById[planId];
		savePublicPlans();
		window.dispatchEvent(new CustomEvent('publicPlansUpdated'));
	}
}

export async function getPublicPlanByIdFromAPI(planId: string | number): Promise<PublicPlan | null> {
	try {
		// If planId is a database ID (number or "db-{number}"), fetch from API
		const dbId = typeof planId === 'number' 
			? planId 
			: planId.startsWith('db-') 
				? parseInt(planId.replace('db-', ''), 10)
				: null;
		
		if (dbId && !isNaN(dbId)) {
			const { getPlanById } = await import('@services/plans');
			const apiPlan = await getPlanById(dbId);
			
			// Extract AI plan details (stored as JSON in database)
			let aiDetails = apiPlan.ai_plan_details || {};
			if (typeof aiDetails === 'string') {
				try {
					aiDetails = JSON.parse(aiDetails);
				} catch {
					aiDetails = {};
				}
			}
			
			// Reconstruct questionnaire data from database columns (not from JSON).
			// Drafts may be saved before garden_size is set, so we must not rely on garden_size alone.
			const hasQuestionnaireData = Boolean(
				apiPlan.garden_size !== null && apiPlan.garden_size !== undefined ||
				apiPlan.before_image_url ||
				apiPlan.style ||
				apiPlan.location ||
				apiPlan.needs_wants ||
				apiPlan.timeline ||
				apiPlan.project_start_date ||
				apiPlan.garden_shape ||
				apiPlan.garden_components ||
				apiPlan.project_focus ||
				apiPlan.garden_uses
			);
			const beforeImageUrl = apiPlan.before_image_url;
			// Parse garden_components JSON if it exists
			let selectedComponents: string[] | undefined = undefined;
			if (apiPlan.garden_components) {
				try {
					const parsed = typeof apiPlan.garden_components === 'string' 
						? JSON.parse(apiPlan.garden_components) 
						: apiPlan.garden_components;
					selectedComponents = Array.isArray(parsed) ? parsed : undefined;
				} catch {
					// Invalid JSON, ignore
				}
			}
			
			let gardenUses: GardenUseKey[] | undefined;
			if (apiPlan.garden_uses) {
				try {
					const parsed = typeof apiPlan.garden_uses === 'string' ? JSON.parse(apiPlan.garden_uses) : apiPlan.garden_uses;
					gardenUses = Array.isArray(parsed) ? parsed : undefined;
				} catch {
					gardenUses = undefined;
				}
			}

			// Decode AI questionnaire storage.
			// Backend stores these as *_json columns; depending on the endpoint/version they may be strings or already-decoded objects.
			const decodeJsonMaybe = (val: unknown): any | undefined => {
				if (val == null) return undefined;
				if (typeof val === 'string') {
					const trimmed = val.trim();
					if (!trimmed) return undefined;
					try {
						return JSON.parse(trimmed);
					} catch {
						return undefined;
					}
				}
				if (typeof val === 'object') return val;
				return undefined;
			};

			const aiQuestionsRaw = (apiPlan as any).ai_questions_json ?? (apiPlan as any).ai_questions;
			const aiAnswersRaw = (apiPlan as any).ai_answers_json ?? (apiPlan as any).ai_answers;
			const aiAnalysisRaw = (apiPlan as any).ai_analysis_json ?? (apiPlan as any).ai_analysis;

			const aiQuestionsDecoded = decodeJsonMaybe(aiQuestionsRaw);
			const aiAnswersDecoded = decodeJsonMaybe(aiAnswersRaw);
			const aiAnalysisDecoded = decodeJsonMaybe(aiAnalysisRaw);

			// Split user comments (ai_q_{idx}_comment) out into a separate map.
			const aiQuestionComments: Record<string, string> = {};
			let aiAnswersClean: Record<string, any> | undefined;
			if (aiAnswersDecoded && typeof aiAnswersDecoded === 'object' && !Array.isArray(aiAnswersDecoded)) {
				aiAnswersClean = {};
				for (const [k, v] of Object.entries(aiAnswersDecoded as Record<string, any>)) {
					const match = k.match(/^ai_q_(\d+)_comment$/);
					if (match) {
						aiQuestionComments[match[1]] = v == null ? '' : String(v);
					} else {
						aiAnswersClean[k] = v;
					}
				}
				if (Object.keys(aiQuestionComments).length === 0) {
					// Keep questionnaire.aiQuestionComments undefined when empty.
				}
			}

			const questionnaireAiAnalysis: AIAnalysis | undefined = (() => {
				if (!aiAnalysisDecoded && !aiQuestionsDecoded) return undefined;
				const base = (aiAnalysisDecoded && typeof aiAnalysisDecoded === 'object' ? aiAnalysisDecoded : {}) as AIAnalysis;
				// Prefer the explicit questions list when present, but handle multiple shapes:
				// - array: [{question, ...}, ...]
				// - object keyed by numeric index: {"8": {question, ...}, ...}
				// - object with suggestedQuestions already inside
				const coerceQuestionsToArray = (decoded: any): any[] | undefined => {
					if (!decoded) return undefined;
					if (Array.isArray(decoded)) return decoded;
					if (decoded && typeof decoded === 'object') {
						if (Array.isArray(decoded.suggestedQuestions)) return decoded.suggestedQuestions;

						// Numeric keys: "0", "1", ...
						const entries = Object.entries(decoded);
						const numericEntries = entries.filter(([k]) => /^-?\d+$/.test(k));
						if (numericEntries.length > 0) {
							return numericEntries
								.sort(([a], [b]) => Number(a) - Number(b))
								.map(([, v]) => v)
								.filter(Boolean);
						}
					}
					return undefined;
				};

				const suggestedQuestionsFromAiQuestions = coerceQuestionsToArray(aiQuestionsDecoded);
				const suggestedQuestionsFromBase = coerceQuestionsToArray((base as any)?.suggestedQuestions) ?? (Array.isArray((base as any)?.suggestedQuestions) ? (base as any).suggestedQuestions : undefined);

				return {
					...base,
					suggestedQuestions: suggestedQuestionsFromAiQuestions ?? suggestedQuestionsFromBase
				};
			})();

			const questionnaire: QuestionnaireData | undefined = hasQuestionnaireData ? {
				gardenSize: apiPlan.garden_size || 0,
				gardenShape: apiPlan.garden_shape || undefined,
				selectedComponents: selectedComponents,
				photoBeforeUrl: beforeImageUrl || undefined, // Load from database column
				styleKey: (apiPlan.style as any) || 'modern',
				needsWants: apiPlan.needs_wants || '',
				maintenanceLevel: apiPlan.maintenance_level || undefined,
				childrenPets: apiPlan.children_pets || undefined,
				budgetRange: (apiPlan.budget_min !== null && apiPlan.budget_min !== undefined && 
				             apiPlan.budget_max !== null && apiPlan.budget_max !== undefined) ? {
					min: apiPlan.budget_min,
					max: apiPlan.budget_max
				} : undefined,
				timeline: apiPlan.timeline || '',
				projectStartDate: apiPlan.project_start_date || undefined,
				location: apiPlan.location || '',
				householdSize: apiPlan.household_size ?? undefined,
				gardenUsers: apiPlan.garden_users ?? undefined,
				plantDensityPreference: apiPlan.plant_density_preference ?? undefined,
				projectFocus: apiPlan.project_focus ?? undefined,
				gardenUses: gardenUses ?? [],
				hostingMaxPeople: apiPlan.hosting_max_people ?? undefined,
				kidsCount: apiPlan.kids_count ?? undefined,
				kidsAges: apiPlan.kids_ages ?? undefined,
				petsDescription: apiPlan.pets_description ?? undefined,
				aiAnalysis: questionnaireAiAnalysis,
				aiAnswers: aiAnswersClean,
				aiQuestionComments: Object.keys(aiQuestionComments).length > 0 ? aiQuestionComments : undefined
			} : undefined;
			
			// Get AI-generated content from JSON (API stores description, features, instructions, tools, pricing at top level)
			const aiGenerated = aiDetails.aiGenerated || (aiDetails.photoAfterUrl || aiDetails.planDescription ? aiDetails : undefined);
			// Instructions from AI plan are often at top level (aiDetails.instructions), not nested
			const instructions = Array.isArray(aiDetails.instructions) ? aiDetails.instructions : (aiGenerated?.instructions ?? []);
			let finalAiGenerated = aiGenerated;
			const requiredSkills = aiDetails.required_skills ?? aiGenerated?.required_skills;
			const recommendedTeamSize = aiDetails.recommended_team_size ?? aiGenerated?.recommended_team_size;
			const timelineTasks = Array.isArray(aiDetails.timeline_tasks)
				? aiDetails.timeline_tasks
				: (Array.isArray(aiGenerated?.timeline_tasks) ? aiGenerated.timeline_tasks : undefined);
			const selectedComponentCoverage = aiDetails.selected_component_coverage ?? aiGenerated?.selected_component_coverage;
			const generationWarnings = Array.isArray(aiDetails.generation_warnings)
				? aiDetails.generation_warnings
				: (Array.isArray(aiGenerated?.generation_warnings) ? aiGenerated.generation_warnings : undefined);
			const skillsSpread = Array.isArray(requiredSkills) && requiredSkills.length > 0 ? { required_skills: requiredSkills } : {};
			const teamSizeSpread = recommendedTeamSize != null && recommendedTeamSize >= 1 ? { recommended_team_size: recommendedTeamSize } : {};
			const timelineTasksSpread = Array.isArray(timelineTasks) && timelineTasks.length > 0 ? { timeline_tasks: timelineTasks } : {};
			const coverageSpread = selectedComponentCoverage ? { selected_component_coverage: selectedComponentCoverage } : {};
			const warningsSpread = Array.isArray(generationWarnings) && generationWarnings.length > 0 ? { generation_warnings: generationWarnings } : {};
			if (apiPlan.after_image_url) {
				if (aiGenerated) {
					finalAiGenerated = {
						...aiGenerated,
						photoAfterUrl: aiGenerated.photoAfterUrl || apiPlan.after_image_url,
						instructions: instructions.length ? instructions : (aiGenerated.instructions ?? []),
						...skillsSpread,
						...teamSizeSpread,
						...timelineTasksSpread,
						...coverageSpread,
						...warningsSpread
					};
				} else {
					finalAiGenerated = {
						photoAfterUrl: apiPlan.after_image_url,
						planDescription: (aiDetails.planDescription ?? aiDetails.description) || '',
						keyFeatures: Array.isArray(aiDetails.features) ? aiDetails.features : [],
						instructions,
						pricingBreakdown: normalizePricingBreakdown(
							(aiDetails.pricingBreakdown ?? aiDetails.pricing) || { min: 0, mid: 0, max: 0, materials: [], labor: [] }
						),
						generatedAt: apiPlan.created_at,
						...skillsSpread,
						...teamSizeSpread,
						...timelineTasksSpread,
						...coverageSpread,
						...warningsSpread
					} as any;
				}
			} else if (instructions.length && finalAiGenerated) {
				finalAiGenerated = { ...finalAiGenerated, instructions, ...skillsSpread, ...teamSizeSpread, ...timelineTasksSpread, ...coverageSpread, ...warningsSpread };
			} else if (instructions.length && !finalAiGenerated) {
				// Plan has implementation instructions from AI but no after image — still expose instructions for timeline
				finalAiGenerated = {
					photoAfterUrl: '',
					planDescription: (aiDetails.planDescription ?? aiDetails.description) || '',
					keyFeatures: Array.isArray(aiDetails.features) ? aiDetails.features : [],
					instructions,
					pricingBreakdown: normalizePricingBreakdown(
						(aiDetails.pricingBreakdown ?? aiDetails.pricing) || { min: 0, mid: 0, max: 0, materials: [], labor: [] }
					),
					generatedAt: apiPlan.created_at,
					...skillsSpread,
					...teamSizeSpread,
					...timelineTasksSpread,
					...coverageSpread,
					...warningsSpread
				} as any;
			}

			if (finalAiGenerated?.pricingBreakdown) {
				finalAiGenerated = {
					...finalAiGenerated,
					pricingBreakdown: normalizePricingBreakdown(finalAiGenerated.pricingBreakdown)
				};
			}
			
			// Get moderation status from database, default to 'visible'
			const dbModerationStatus = apiPlan.moderation_status || 'visible';
			
			// Revisions (history of AI-generated designs)
			let revisions: PlanRevision[] = [];
			if (apiPlan.revisions && Array.isArray(apiPlan.revisions)) {
				revisions = apiPlan.revisions.map((r: any) => ({
					photoAfterUrl: r.photoAfterUrl ?? '',
					planDescription: r.planDescription ?? '',
					keyFeatures: Array.isArray(r.keyFeatures) ? r.keyFeatures : [],
					instructions: Array.isArray(r.instructions) ? r.instructions : [],
					...(Array.isArray(r.timeline_tasks) && r.timeline_tasks.length > 0 ? { timeline_tasks: r.timeline_tasks } : {}),
					tools: r.tools,
					pricingBreakdown: normalizePricingBreakdown(
						r.pricingBreakdown ?? { min: 0, mid: 0, max: 0, materials: [], labor: [] }
					),
					...(Array.isArray(r.required_skills) && r.required_skills.length > 0 ? { required_skills: r.required_skills } : {}),
					...(r.recommended_team_size != null && r.recommended_team_size >= 1 ? { recommended_team_size: r.recommended_team_size } : {}),
					generatedAt: r.generatedAt ?? apiPlan.updated_at ?? apiPlan.created_at,
					editInstructions: r.editInstructions
				}));
			}
			
			// Convert API plan format to PublicPlan format
			const publicPlan: PublicPlan = {
				id: `db-${apiPlan.id}`,
				databaseId: apiPlan.id,
				seekerUserId: String(apiPlan.user_id),
				title: apiPlan.title,
				city: apiPlan.location || '',
				contactEmail: apiPlan.contact_email || '', // Load from database column
				styleKey: (apiPlan.style as any) || 'modern',
				notes: apiPlan.description,
				photoBeforeUrl: apiPlan.before_image_url,
				status: apiPlan.status === 'published' ? 'active' : apiPlan.status === 'draft' ? 'draft' : 'closed',
				createdAt: apiPlan.created_at,
				questionnaire: questionnaire,
				aiGenerated: finalAiGenerated,
				revisions: revisions.length > 0 ? revisions : undefined,
				moderationStatus: dbModerationStatus as PlanModerationStatus
			};

			// Keep before-photo URL aligned on plan root vs questionnaire (some API paths populate only one).
			const beforeFromQ = publicPlan.questionnaire?.photoBeforeUrl?.trim();
			const beforeFromRoot = (publicPlan.photoBeforeUrl || '').trim();
			if (publicPlan.questionnaire && beforeFromRoot && !beforeFromQ) {
				publicPlan.questionnaire = { ...publicPlan.questionnaire, photoBeforeUrl: beforeFromRoot };
			}
			if (beforeFromQ && !beforeFromRoot) {
				publicPlan.photoBeforeUrl = beforeFromQ;
			}
			
			// Add/update in-memory cache and persist so list views and publish flow stay in sync
			try {
				const existingIndex = publicPlans.findIndex(p => p.id === publicPlan.id || p.databaseId === publicPlan.databaseId);
				if (existingIndex >= 0) {
					publicPlans[existingIndex] = publicPlan;
				} else {
					publicPlans.push(publicPlan);
				}
				publicPlansById[publicPlan.id] = publicPlan;
				savePublicPlans();
			} catch (err) {
				// Ignore localStorage errors (e.g. quota), keep in memory only
			}

			return publicPlan;
		}
	} catch (err) {
		console.error('Failed to load plan from API:', err);
	}
	return null;
}

export function getPublicPlanById(planId: string, options?: { includeHidden?: boolean }): PublicPlan | undefined {
	const plan = getPublicPlans().find(p => p.id === planId);
	if (!plan) return undefined;
	if (!options?.includeHidden && plan.moderationStatus === 'hidden') return undefined;
	return plan;
}


